import { Browser } from '@capacitor/browser'
import { Box, Button, List, Typography } from '@mui/material'
import React, { useState } from 'react'
import { Divider, Icons, InformationModal, MenuListItem } from 'utoniq-ui'

import { BaseLayout, Head } from '@/components/layouts'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { App } from '@/core/native'

export * from './fragments'

export type IntegrationType = 'twitter'
export type IntegrationInfo = {
  loading: boolean
  connected: boolean
  displayName: string
  isBound: boolean
}
export type State = {
  integrations: {
    twitter: IntegrationInfo
  }
}

export type Action = {
  onConnectTwitter: () => Promise<void>
  onDisConnectTwitter: () => Promise<void>
}

export const IntegrationsTemplate: Template<State, Action> = ({
  state,
  onConnectTwitter,
  onDisConnectTwitter,
}) => {
  const { t } = useTranslation('integrations')
  const { routerNames, routerPages } = useRouter()

  return (
    <BaseLayout
      header={{
        title: t('title'),
        back: true,
        defaultBackPage: routerPages.AccountSetting,
      }}
    >
      <Head title={routerNames.Integrations} />
      <List component="nav" sx={{ bgcolor: 'grey.50', mb: 7 }}>
        <LinkAccountCell
          type="twitter"
          {...state.integrations['twitter']}
          onConnect={onConnectTwitter}
          onDisConnect={onDisConnectTwitter}
        />
      </List>
    </BaseLayout>
  )
}

type LinkAccountCellProps = {
  type: IntegrationType
  onConnect: () => Promise<void>
  onDisConnect: () => Promise<void>
} & IntegrationInfo
const LinkAccountCell: Cell<LinkAccountCellProps> = ({
  type,
  onConnect,
  onDisConnect,
  loading,
  connected,
  displayName,
  isBound,
}) => {
  const { t } = useTranslation('integrations')
  const [connectOpen, setConnectOpen] = useState(false)
  const [disConnectOpen, setDisConnectOpen] = useState(false)
  const [loadingDisConnect, setLoadingDisConnect] = useState(false)
  const isWeb = App.getPlatform() === 'web'
  const { routerPages } = useRouter()

  const handleConnect = isWeb ? onConnect : () => setConnectOpen(true)
  const handleDisConnect = async () => {
    setLoadingDisConnect(true)
    await onDisConnect()
    setLoadingDisConnect(false)
    setDisConnectOpen(false)
  }

  const onClick = connected ? () => setDisConnectOpen(true) : handleConnect

  const SNSIcon = (type: IntegrationType) => {
    switch (type) {
      case 'twitter':
        return (
          <Icons.Twitter
            fontSize="medium"
            color={connected ? undefined : 'disabled'}
          />
        )
    }
  }

  return (
    <CellFrame>
      <MenuListItem
        component={Box}
        title={type}
        icon={SNSIcon(type)}
        primaryTypographyProps={{ variant: 'body1' }}
        secondaryAction={
          <Typography variant="subtitle2">{displayName}</Typography>
        }
        onClick={onClick}
        disabled={isBound || loading}
      />
      <Divider />
      <InformationModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        title={t('connectDialogTitle', { provider: type })}
        content={t('connectDialogContent', { provider: type })}
        cancelText={t('cancel')}
        buttons={
          <Button
            color="primary"
            sx={{ minWidth: 212 }}
            size="large"
            onClick={() => {
              Browser.open({
                url: `${config.domain}${routerPages.Integrations}`,
              }).then(() => setConnectOpen(false))
            }}
          >
            {t('openBrowser')}
          </Button>
        }
      />
      <InformationModal
        open={disConnectOpen}
        onClose={() => setDisConnectOpen(false)}
        title={t('disConnectDialogTitle')}
        content={t('disConnectDialogContent', { provider: type })}
        cancelText={t('cancel')}
        buttons={
          <Button
            color="primary"
            sx={{ minWidth: 212 }}
            size="large"
            onClick={handleDisConnect}
            loading={loadingDisConnect}
          >
            {t('ok')}
          </Button>
        }
      />
    </CellFrame>
  )
}
